import PropTypes from 'prop-types';
import { Collapse, Grid } from '@material-ui/core';
import { load } from 'model/payerAudit';
import { load as loadSSOErrorLog } from 'model/ssoErrorLog';
import { makeStyles } from '@material-ui/core/styles';
import { profileSelector, selectorAsName } from 'selectors/payer';
import { selectorCount, selectorGroupedByDays } from 'selectors/payerAudit';
import {
  selectorCount as selectorCountSSOErrorLog,
  selectorGroupedByDays as selectorGroupedByDaysSSOErrorLog,
} from 'selectors/ssoErrorLog';
import { useSelector, useDispatch } from 'react-redux';
import { fspConfigValuesSelector } from 'selectors/fspConfigValues';
import Audit from 'components/common/Audit';
import AuditDetailRowRecord from 'components/common/Audit/config/shared/AuditDetailRowRecord';
import AuditDrawerAppBarEndAdornment from 'components/common/Audit/config/shared/AuditDrawerAppBarEndAdornment';
import { useEffect, useMemo, useState } from 'react';
import { load as loadFspConfigValues } from 'model/fspConfigValues';
import AuditDrawerAppBarTitle from '../../Audit/config/shared/AuditDrawerAppBarTitle';
import LoginFailuresDrawerAppBarTitle from './LoginFailures/LoginFailuresDrawerAppBarTitle';
import Limits from './Limits';
import Tickets from './Tickets';
import Notes from './Note';
import ViewAs from './ViewAs';
import TierOneSupport from './TierOneSupport';
import {
  getInitialValues,
  ON,
  TIER_ONE_SUPPORT_ENABLED_OFF_ON,
} from './TierOneSupport/consts';
import { PayerUserButtonProvider } from '../PayerOrPayerUserButton';
import useFlags from '../../../implementation/Fsp/Detail/common/hooks/useFeatureFlags';

const useStyles = makeStyles((theme) => ({
  actions: {
    marginRight: theme.spacing(4),
    marginBottom: theme.spacing(0.5),
  },
}));

const ProfileButtons = ({ expanded, fspId, id }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const item = useSelector((state) => profileSelector(state, id));

  const selectFspConfig = useMemo(
    () => fspConfigValuesSelector(fspId),
    [fspId]
  );
  const fspConfigValues = useSelector(selectFspConfig);
  const tierOneSupportEnabled =
    getInitialValues(fspConfigValues)[TIER_ONE_SUPPORT_ENABLED_OFF_ON] === ON;

  useEffect(() => {
    if (expanded && fspId) {
      dispatch(loadFspConfigValues({ fspId }));
    }
  }, [dispatch, expanded, fspId]);

  const { data, loading: flagLoading, error } = useFlags('ontrac-payer-notes');

  const [payerNotesEnabled, setPayerNotesEnabled] = useState(false);

  useEffect(() => {
    if (!flagLoading && data !== null && data.FlagValue !== null) {
      setPayerNotesEnabled(data.FlagValue);
    }
  }, [data, flagLoading, error]);

  return !item.isEmpty ? (
    <Collapse className={classes.actions} in={expanded}>
      <Grid alignItems="center" container justifyContent="flex-end" spacing={1}>
        <Grid item>
          {tierOneSupportEnabled && <TierOneSupport fspId={fspId} />}
        </Grid>
        <Grid item>
          <Tickets fspId={item.zfspId} id={id} />
        </Grid>
        <Grid item>
          <Limits fspId={fspId} id={id} limitType={item.limitType} />
        </Grid>
        <Grid item>
          <Audit
            action={load}
            countSelector={selectorCount}
            DrawerEndAdornment={AuditDrawerAppBarEndAdornment}
            item={item}
            Row={AuditDetailRowRecord}
            selectorGroupedByDays={selectorGroupedByDays}
            selectorId={selectorAsName}
            Title={AuditDrawerAppBarTitle}
          />
        </Grid>
        <Grid item>
          <Audit
            action={loadSSOErrorLog}
            buttonLabel="Login Failures"
            countSelector={selectorCountSSOErrorLog}
            item={item}
            Row={AuditDetailRowRecord}
            selectorGroupedByDays={selectorGroupedByDaysSSOErrorLog}
            selectorId={selectorAsName}
            Title={LoginFailuresDrawerAppBarTitle}
          />
        </Grid>
        <Grid item>
          <PayerUserButtonProvider>
            <ViewAs fspId={fspId} payerId={id} payerType={item.payerType} />
          </PayerUserButtonProvider>
        </Grid>
        <Grid item>
          {payerNotesEnabled && <Notes fspId={fspId} payerId={id} />}
        </Grid>
      </Grid>
    </Collapse>
  ) : null;
};

ProfileButtons.propTypes = {
  expanded: PropTypes.bool.isRequired,
  fspId: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
};

export default ProfileButtons;
